import { useCallback, useEffect, useRef } from 'react';

import { useFocusEffect } from '@react-navigation/native';
import { useRouter } from 'expo-router';
import { useStoreContext } from 'features/store';

import { routes } from 'utils/routing';

/**
 * Control (deeplinks-flow-feature): open the legacy full store locator once, then
 * dismiss this deeplink host when the guest closes the locator without picking a
 * store — otherwise the underlying CardModal loader / LocationEmptyState stays up.
 *
 * After a successful store selection, `noStoreSelected` flips false before we
 * dismiss, so the host stays mounted for `redirectUrl` re-check.
 */
export function useControlNoStoreStoreLocatorRedirect(redirectUrl: string) {
  const router = useRouter();
  const { noStoreSelected } = useStoreContext();
  const didOpenLocatorRef = useRef(false);
  const hasLeftForLocatorRef = useRef(false);
  const noStoreSelectedRef = useRef(noStoreSelected);
  noStoreSelectedRef.current = noStoreSelected;

  useEffect(() => {
    if (didOpenLocatorRef.current) {
      return;
    }
    didOpenLocatorRef.current = true;
    router.navigate({
      pathname: routes.storeLocator,
      params: { redirectUrl },
    });
  }, [redirectUrl, router]);

  useFocusEffect(
    useCallback(() => {
      // First focus (and any focus before we've actually left for the locator)
      // must not dismiss — only a return focus after the locator counts as cancel.
      if (!didOpenLocatorRef.current || !hasLeftForLocatorRef.current) {
        return () => {
          if (didOpenLocatorRef.current) {
            hasLeftForLocatorRef.current = true;
          }
        };
      }

      // Defer so a successful store pick can flip noStoreSelected before we
      // treat this focus as a cancel.
      const timeoutId = setTimeout(() => {
        if (!noStoreSelectedRef.current) {
          return;
        }
        if (router.canDismiss()) {
          router.dismiss();
        } else {
          router.replace(routes.base);
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }, [router])
  );
}
